import React, { useEffect, useState } from 'react';

import { useDispatch, useSelector } from 'react-redux';

import { getDepartments, getEmployees } from '../../../features/actions/actionPlanActions';
import {
  selectDepartments,
  selectDropdownActionsList,
  selectEmployees,
} from '../../../features/actions/actionPlanSlice';
import { assignmentSchema } from '../validationSchemas';
import { Container } from './EmployeeSteps.styles';
import { Step1ActionDate, Step2Selection, Step3KpiConfig, Step4Compensation } from './steps';

const getEmptyForm = (type) => ({
  action: null,
  effectiveDate: null,
  actionType: null,
  estimationType: null,
  selectedEmployees: [],
  selectedDepartment: [],
  kpiConfig: {
    duration: null,
    currency: { value: 'AMD', label: 'AMD' },
    qLowAmount: '',
    qTargetAmount: '',
    qHighAmount: '',
    lowAmount: '',
    targetAmount: '',
    highAmount: '',
  },
  compensationConfig: {
    belowLow: undefined,
    lowToTarget: undefined,
    targetToHigh: undefined,
    aboveHigh: undefined,
  },
  assignmentType: type,
});

const EmployeeSteps = ({
  currentStep,
  assignmentType,
  setIsValid = () => {},
  isReadOnly,
  onChange = () => {},
  triggerValidation,
}) => {
  const [formData, setFormData] = useState(() => getEmptyForm(assignmentType));
  const actionsList = useSelector(selectDropdownActionsList);

  const [errors, setErrors] = useState({});
  const dispatch = useDispatch();

  const employees = useSelector(selectEmployees);
  const departments = useSelector(selectDepartments);

  const [employeeData, setEmployeeData] = useState([]);
  const [departmentData, setDepartmentData] = useState([]);
  const [submitted, setSubmitted] = useState(false);
  const [resetSelectTrigger, setResetSelectTrigger] = useState([]);

  useEffect(() => {
    const comp = formData.compensationConfig;

    const compensationConfig =
      assignmentType === 'employee'
        ? {
            ...(comp.belowLow != null && {
              belowLowDeduction: Number(comp.belowLow),
            }),
            ...(comp.lowToTarget != null && {
              lowToTargetDeduction: Number(comp.lowToTarget),
            }),
            ...(comp.targetToHigh != null && {
              targetToHighBonus: Number(comp.targetToHigh),
            }),
            ...(comp.aboveHigh != null && {
              aboveHighBonus: Number(comp.aboveHigh),
            }),
          }
        : undefined;

    const employeeIds = formData.selectedEmployees?.map((e) => e.id) || [];
    const departmentIds = formData.selectedDepartment?.map((d) => d.id) || [];

    const payload = {
      actionId: formData.action?.value,
      actionType: formData.actionType?.value?.toUpperCase(),
      estimationType: formData.estimationType?.value?.toUpperCase(),
      effectiveDate: formData.effectiveDate,

      ...(assignmentType === 'employee' ? { employeeIds } : { departmentIds }),

      kpiConfig: {
        periodType: formData.kpiConfig.duration?.value,
        quantityLow: formData.kpiConfig.qLowAmount
          ? Number(formData.kpiConfig.qLowAmount)
          : null,
        quantityTarget: formData.kpiConfig.qTargetAmount
          ? Number(formData.kpiConfig.qTargetAmount)
          : null,
        quantityHigh: formData.kpiConfig.qHighAmount
          ? Number(formData.kpiConfig.qHighAmount)
          : null,
        financialLow: formData.kpiConfig.lowAmount
          ? Number(formData.kpiConfig.lowAmount)
          : null,
        financialTarget: formData.kpiConfig.targetAmount
          ? Number(formData.kpiConfig.targetAmount)
          : null,
        financialHigh: formData.kpiConfig.highAmount
          ? Number(formData.kpiConfig.highAmount)
          : null,
        currency: formData.kpiConfig.currency?.value,
      },

      ...(compensationConfig && { compensationConfig }),
    };

    onChange(payload);
  }, [formData, assignmentType]);

  useEffect(() => {
    dispatch(getEmployees({ page: 0, size: 100 }));
  }, [dispatch]);

  useEffect(() => {
    dispatch(getDepartments({ page: 0, size: 100 }));
  }, [dispatch]);

  useEffect(() => {
    if (employees?.length) {
      const mappedEmps = employees.map((e) => ({
        id: e.uuid || e.id,
        uuid: e.uuid || e.id,
        label: `${e.name} ${e.surname}`,
        name: `${e.name} ${e.surname}`,
      }));
      setEmployeeData(mappedEmps);
    }
  }, [employees]);

  useEffect(() => {
    if (departments?.length) {
      const mappedDepts = departments.map((d) => ({
        id: d.uuid || d.id,
        uuid: d.uuid || d.id,
        label: d.name,
        name: d.name,
      }));
      setDepartmentData(mappedDepts);
    }
  }, [departments]);

  useEffect(() => {
    if (!triggerValidation) return;

    const runValidation = async () => {
      setSubmitted(true);
      const isValid = await validateForm();
      setIsValid(isValid);
    };

    runValidation();
  }, [triggerValidation]);

  useEffect(() => {
    setSubmitted(false);
    setErrors({});
  }, [currentStep]);

  const validateForm = async () => {
    const fieldsByStep = {
      1: ['action', 'effectiveDate', 'actionType', 'estimationType'],
      2: assignmentType === 'employee' ? ['selectedEmployees'] : ['selectedDepartment'],
      3: [
        'kpiConfig.duration',
        'kpiConfig.qLowAmount',
        'kpiConfig.qTargetAmount',
        'kpiConfig.qHighAmount',
        'kpiConfig.lowAmount',
        'kpiConfig.targetAmount',
        'kpiConfig.highAmount',
        'kpiConfig.currency',
      ],
      4: [
        'compensationConfig.belowLow',
        'compensationConfig.lowToTarget',
        'compensationConfig.targetToHigh',
        'compensationConfig.aboveHigh',
      ],
    };

    try {
      await assignmentSchema.validate(formData, {
        abortEarly: false,
        context: {
          estType: formData.estimationType?.value,
          assignmentType,
        },
      });

      setErrors({});
      return true;
    } catch (err) {
      const validationErrors = {};
      let stepHasError = false;

      if (err.inner) {
        err.inner.forEach((error) => {
          validationErrors[error.path] = error.message;

          const isCurrentStepField = fieldsByStep[currentStep]?.some(
            (field) => error.path === field || error.path.startsWith(`${field}.`)
          );

          if (isCurrentStepField) {
            stepHasError = true;
          }
        });
      }

      setErrors(validationErrors);
      return !stepHasError;
    }
  };

  const handleFieldChange = async (field, value) => {
    const newFormData = { ...formData, [field]: value };
    setFormData(newFormData);

    if (submitted) {
      try {
        await assignmentSchema.validateAt(field, newFormData, {
          context: {
            estType: newFormData.estimationType?.value,
            assignmentType,
          },
        });

        setErrors((prev) => {
          const newErrors = { ...prev };
          delete newErrors[field];
          return newErrors;
        });
      } catch (err) {
        setErrors((prev) => ({ ...prev, [field]: err.message }));
      }
    }
  };

  const handleKpiChange = async (field, value) => {
    const numericFields = [
      'qLowAmount',
      'qTargetAmount',
      'qHighAmount',
      'lowAmount',
      'targetAmount',
      'highAmount',
    ];

    const finalValue = numericFields.includes(field)
      ? value === '' || value === null
        ? undefined
        : Number(value)
      : value;

    const newKpiConfig = {
      ...formData.kpiConfig,
      [field]: finalValue,
    };
    const newFormData = { ...formData, kpiConfig: newKpiConfig };

    setFormData(newFormData);

    if (submitted) {
      const fieldsToValidate = [`kpiConfig.${field}`];

      if (['qLowAmount', 'qTargetAmount', 'qHighAmount'].includes(field)) {
        fieldsToValidate.push(
          'kpiConfig.qLowAmount',
          'kpiConfig.qTargetAmount',
          'kpiConfig.qHighAmount'
        );
      }

      if (['lowAmount', 'targetAmount', 'highAmount'].includes(field)) {
        fieldsToValidate.push(
          'kpiConfig.lowAmount',
          'kpiConfig.targetAmount',
          'kpiConfig.highAmount'
        );
      }

      const updatedErrors = { ...errors };

      for (const fieldPath of fieldsToValidate) {
        try {
          await assignmentSchema.validateAt(fieldPath, newFormData, {
            context: {
              estType: newFormData.estimationType?.value,
              assignmentType,
            },
          });

          delete updatedErrors[fieldPath];
        } catch (err) {
          updatedErrors[fieldPath] = err.message;
        }
      }

      setErrors(updatedErrors);
    }
  };

  const removeSelectedItem = (itemId) => {
    if (assignmentType === 'employee') {
      const updated = formData.selectedEmployees.filter((emp) => emp.id !== itemId);
      handleFieldChange('selectedEmployees', updated);
    } else {
      const updated = formData.selectedDepartment.filter((dept) => dept.id !== itemId);
      handleFieldChange('selectedDepartment', updated);
    }
  };

  const handleSelectWithAll = (_ids, full) => {
    const fieldName =
      assignmentType === 'employee' ? 'selectedEmployees' : 'selectedDepartment';
    const allOptions = assignmentType === 'employee' ? employeeData : departmentData;
    const currentSelection = formData[fieldName];

    if (full.length === 0 && allOptions.length > 0) {
      if (currentSelection.length === allOptions.length) {
        handleFieldChange(fieldName, []);
      } else if (currentSelection.length === 0) {
        handleFieldChange(fieldName, allOptions);
      } else {
        handleFieldChange(fieldName, []);
      }
    } else {
      handleFieldChange(fieldName, full);
    }
  };

  useEffect(() => {
    setFormData(getEmptyForm(assignmentType));
    setErrors({});
    setSubmitted(false);
    setResetSelectTrigger([]);
  }, [assignmentType]);

  useEffect(() => {
    const currentSelection =
      assignmentType === 'employee' ? formData.selectedEmployees : formData.selectedDepartment;
    if (currentSelection?.length === 0) {
      setResetSelectTrigger([]);
    }
  }, [formData.selectedEmployees, formData.selectedDepartment, assignmentType]);

  const handleCompChange = async (field, value) => {
    const numericValue = value === '' ? undefined : Number(value);

    const newCompConfig = {
      ...formData.compensationConfig,
      [field]: numericValue,
    };
    const newFormData = { ...formData, compensationConfig: newCompConfig };

    setFormData(newFormData);

    if (submitted) {
      try {
        await assignmentSchema.validateAt(`compensationConfig.${field}`, newFormData, {
          context: {
            estType: formData.estimationType?.value,
            assignmentType,
          },
        });
        setErrors((prev) => {
          const newErrors = { ...prev };
          delete newErrors[`compensationConfig.${field}`];
          return newErrors;
        });
      } catch (err) {
        setErrors((prev) => ({
          ...prev,
          [`compensationConfig.${field}`]: err.message,
        }));
      }
    }
  };

  const stepsContent = {
    1: (
      <Step1ActionDate
        formData={formData}
        errors={errors}
        submitted={submitted}
        isReadOnly={isReadOnly}
        actionsList={actionsList}
        handleFieldChange={handleFieldChange}
        handleKpiChange={handleKpiChange}
      />
    ),
    2: (
      <Step2Selection
        formData={formData}
        errors={errors}
        submitted={submitted}
        assignmentType={assignmentType}
        employeeData={employeeData}
        departmentData={departmentData}
        handleSelectWithAll={handleSelectWithAll}
        removeSelectedItem={removeSelectedItem}
        resetSelectTrigger={resetSelectTrigger}
      />
    ),
    3: (
      <Step3KpiConfig
        formData={formData}
        errors={errors}
        submitted={submitted}
        isReadOnly={isReadOnly}
        handleKpiChange={handleKpiChange}
      />
    ),
    4:
      assignmentType === 'employee' ? (
        <Step4Compensation
          formData={formData}
          errors={errors}
          submitted={submitted}
          isReadOnly={isReadOnly}
          handleCompChange={handleCompChange}
        />
      ) : null,
  };

  return <Container>{stepsContent[currentStep] || null}</Container>;
};

export default EmployeeSteps;
